import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import type { RoadmapItem, RoadmapItemStatus, RoadmapSubtask } from "../../types/roadmap";
import { invokeRoadmap } from "../../services/roadmapApi";
import {
  ROADMAP_STATUS_LABEL,
  ROADMAP_STATUS_ORDER,
  roadmapStatusTagColor,
} from "./roadmapStatusConfig";
import { RoadmapAssistantSyncPanel } from "./RoadmapAssistantSyncPanel";
import { RoadmapCompanyQuestions } from "./RoadmapCompanyQuestions";
import { RoadmapWorkingNotesEditor } from "./RoadmapWorkingNotesEditor";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const STATUS_OPTIONS: { value: RoadmapItemStatus; label: string }[] = ROADMAP_STATUS_ORDER.map(
  (value) => ({ value, label: ROADMAP_STATUS_LABEL[value] })
);

interface RoadmapItemDetailModalProps {
  open: boolean;
  item: RoadmapItem | null;
  onClose: () => void;
  editSecret: string;
  officialUnlocked: boolean;
  onRefresh: () => Promise<void>;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export const RoadmapItemDetailModal: React.FC<RoadmapItemDetailModalProps> = ({
  open,
  item,
  onClose,
  editSecret,
  officialUnlocked,
  onRefresh,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
}) => {
  const [notes, setNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [newSubtask, setNewSubtask] = useState("");
  const [officialTitle, setOfficialTitle] = useState("");
  const [officialDescription, setOfficialDescription] = useState("");
  const [officialStatus, setOfficialStatus] = useState<RoadmapItemStatus>("planned");
  const [officialSubmitting, setOfficialSubmitting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [finishAt, setFinishAt] = useState<Dayjs | null>(null);
  const [finishSaving, setFinishSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setNotes(item.working_notes);
      setFinishAt(item.expected_finish_at ? dayjs(item.expected_finish_at) : null);
    }
  }, [item?.id, item?.working_notes, item?.expected_finish_at, open]);

  useEffect(() => {
    if (open && item) {
      setOfficialTitle(item.title);
      setOfficialDescription(item.description);
      setOfficialStatus(item.status);
    }
  }, [open, item?.id]);

  const subtasks = useMemo(() => {
    if (!item) return [];
    const list = item.roadmap_subtasks ?? [];
    return [...list].sort((a, b) => a.sort_order - b.sort_order);
  }, [item?.roadmap_subtasks]);

  const companyQuestions = item?.roadmap_company_questions ?? [];

  if (!item) {
    return null;
  }

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await invokeRoadmap({
        action: "update_working_notes",
        itemId: item.id,
        workingNotes: notes,
      });
      message.success("Notes saved");
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setNotesSaving(false);
    }
  };

  const saveExpectedFinish = async () => {
    setFinishSaving(true);
    try {
      await invokeRoadmap({
        action: "update_expected_finish",
        itemId: item.id,
        expectedFinishAt: finishAt ? finishAt.toISOString() : null,
      });
      message.success("Expected finish saved");
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setFinishSaving(false);
    }
  };

  const togglePriority = async () => {
    try {
      await invokeRoadmap({ action: "toggle_priority", itemId: item.id });
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Update failed");
    }
  };

  const addSubtask = async () => {
    const t = newSubtask.trim();
    if (!t) return;
    try {
      await invokeRoadmap({ action: "add_subtask", itemId: item.id, title: t });
      setNewSubtask("");
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Failed to add");
    }
  };

  const setSubtaskDone = async (sub: RoadmapSubtask, done: boolean) => {
    try {
      await invokeRoadmap({ action: "update_subtask", subtaskId: sub.id, done });
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Update failed");
    }
  };

  const removeSubtask = async (subtaskId: string) => {
    try {
      await invokeRoadmap({ action: "delete_subtask", subtaskId });
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const saveOfficialFields = async () => {
    const t = officialTitle.trim();
    if (!t) {
      message.warning("Title is required");
      return;
    }
    setOfficialSubmitting(true);
    try {
      await invokeRoadmap({
        action: "update_official",
        editSecret,
        itemId: item.id,
        title: t,
        description: officialDescription,
        status: officialStatus,
      });
      message.success("Official fields saved");
      await onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Update failed");
    } finally {
      setOfficialSubmitting(false);
    }
  };

  /** Status is persisted immediately; title/description still use "Save official changes". */
  const saveStatusOnly = async (next: RoadmapItemStatus) => {
    const previous = officialStatus;
    setOfficialStatus(next);
    setStatusSaving(true);
    try {
      await invokeRoadmap({
        action: "update_official",
        editSecret,
        itemId: item.id,
        status: next,
      });
      await onRefresh();
    } catch (e) {
      setOfficialStatus(previous);
      message.error(e instanceof Error ? e.message : "Could not save status");
    } finally {
      setStatusSaving(false);
    }
  };

  const deleteItem = () => {
    Modal.confirm({
      title: "Delete this roadmap item?",
      okType: "danger",
      onOk: async () => {
        try {
          await invokeRoadmap({
            action: "delete_item",
            editSecret,
            itemId: item.id,
          });
          message.success("Deleted");
          onClose();
          await onRefresh();
        } catch (e) {
          message.error(e instanceof Error ? e.message : "Delete failed");
        }
      },
    });
  };

  const doneSubCount = subtasks.filter((s) => s.done).length;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      styles={{ body: { maxHeight: "calc(100vh - 120px)", overflowY: "auto" } }}
      key={item.id}
    >
      <Space align="start" style={{ width: "100%", marginBottom: 8 }} size="middle">
        <Button
          type="text"
          size="large"
          aria-label={item.is_prioritized ? "Unprioritise" : "Prioritise"}
          icon={
            item.is_prioritized ? (
              <StarFilled style={{ color: "#faad14", fontSize: 22 }} />
            ) : (
              <StarOutlined style={{ fontSize: 22 }} />
            )
          }
          onClick={togglePriority}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {officialUnlocked ? (
            <Input
              value={officialTitle}
              onChange={(e) => setOfficialTitle(e.target.value)}
              placeholder="Title"
              style={{
                fontSize: 20,
                fontWeight: 600,
                marginBottom: 8,
              }}
            />
          ) : (
            <Title level={4} style={{ margin: "0 0 8px" }}>
              {item.title}
            </Title>
          )}
          <Space size={6} wrap align="center">
            {officialUnlocked ? (
              <Select
                value={officialStatus}
                onChange={(v) => void saveStatusOnly(v)}
                disabled={statusSaving}
                loading={statusSaving}
                options={STATUS_OPTIONS}
                style={{ minWidth: 180 }}
                optionRender={(opt) => (
                  <Space size={6}>
                    <Tag color={roadmapStatusTagColor(opt.value as RoadmapItemStatus)} style={{ margin: 0 }}>
                      {opt.label}
                    </Tag>
                  </Space>
                )}
                labelRender={(props) => (
                  <Tag color={roadmapStatusTagColor(props.value as RoadmapItemStatus)} style={{ margin: 0 }}>
                    {props.label}
                  </Tag>
                )}
              />
            ) : (
              <Tag color={roadmapStatusTagColor(item.status)}>{ROADMAP_STATUS_LABEL[item.status]}</Tag>
            )}
            {item.is_prioritized && <Tag color="gold">Prioritised</Tag>}
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID: {item.id}
            </Text>
          </Space>
        </div>
      </Space>

      {officialUnlocked && (
        <Space wrap style={{ marginBottom: 10 }} size="small">
          <Button icon={<ArrowUpOutlined />} disabled={!canMoveUp} onClick={onMoveUp}>
            Move up
          </Button>
          <Button icon={<ArrowDownOutlined />} disabled={!canMoveDown} onClick={onMoveDown}>
            Move down
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={deleteItem}>
            Delete
          </Button>
        </Space>
      )}

      {officialUnlocked && (
        <Collapse
          size="small"
          bordered={false}
          style={{ marginBottom: 10, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 8 }}
          defaultActiveKey={[]}
          items={[
            {
              key: "official-desc",
              label: (
                <Text strong style={{ fontSize: 13 }}>
                  Description & save official changes
                </Text>
              ),
              children: (
                <>
                  <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
                    Status saves when you pick it above. Save below updates title and description (uses your unlock
                    secret).
                  </Text>
                  <TextArea
                    rows={3}
                    value={officialDescription}
                    onChange={(e) => setOfficialDescription(e.target.value)}
                    placeholder="Short description"
                    style={{ marginBottom: 8 }}
                  />
                  <Button type="primary" size="small" loading={officialSubmitting} onClick={() => void saveOfficialFields()}>
                    Save official changes
                  </Button>
                </>
              ),
            },
          ]}
        />
      )}

      {!officialUnlocked && item.description ? (
        <Collapse
          size="small"
          bordered={false}
          style={{ marginBottom: 10, background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8 }}
          defaultActiveKey={[]}
          items={[
            {
              key: "desc-readonly",
              label: (
                <Text type="secondary" strong style={{ fontSize: 13 }}>
                  Description
                </Text>
              ),
              children: (
                <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 13 }}>
                  {item.description}
                </Paragraph>
              ),
            },
          ]}
        />
      ) : null}

      {officialUnlocked && (
        <RoadmapAssistantSyncPanel
          item={item}
          subtasks={subtasks}
          onRefresh={onRefresh}
          officialUnlocked={officialUnlocked}
        />
      )}

      <div style={{ marginBottom: 10 }}>
        <Text strong style={{ fontSize: 13 }}>
          Expected finish
        </Text>
        <div style={{ marginTop: 4 }}>
          <Space wrap align="center">
            <DatePicker
              value={finishAt}
              onChange={(d) => setFinishAt(d)}
              allowClear
              format="YYYY-MM-DD"
              style={{ minWidth: 140 }}
            />
            <Button type="default" size="small" loading={finishSaving} onClick={saveExpectedFinish}>
              Save date
            </Button>
          </Space>
          {item.expected_finish_at &&
            item.status !== "done" &&
            item.status !== "wont_do" &&
            dayjs(item.expected_finish_at).startOf("day").isBefore(dayjs().startOf("day")) && (
              <Tag color="red" style={{ marginTop: 8 }}>
                Overdue
              </Tag>
            )}
        </div>
      </div>

      <RoadmapCompanyQuestions itemId={item.id} questions={companyQuestions} onRefresh={onRefresh} />

      {officialUnlocked && (
        <RoadmapWorkingNotesEditor
          value={notes}
          onChange={setNotes}
          saving={notesSaving}
          onSave={() => void saveNotes()}
        />
      )}

      <div style={{ marginBottom: 4 }}>
        <Text strong style={{ fontSize: 13 }}>
          Sub-todos{" "}
          <Text type="secondary" style={{ fontWeight: "normal" }}>
            ({doneSubCount}/{subtasks.length} done)
          </Text>
        </Text>
        <div style={{ marginTop: 8 }}>
          {subtasks.map((s) => (
            <div
              key={s.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <Checkbox
                checked={s.done}
                style={{ marginTop: 4 }}
                onChange={(e) => setSubtaskDone(s, e.target.checked)}
              >
                <span style={{ whiteSpace: "pre-wrap" }}>{s.title}</span>
              </Checkbox>
              <Button type="link" danger size="small" onClick={() => removeSubtask(s.id)} style={{ flexShrink: 0 }}>
                Remove
              </Button>
            </div>
          ))}
          <Space.Compact style={{ marginTop: 8, width: "100%", maxWidth: 480 }}>
            <Input
              placeholder="New sub-todo"
              value={newSubtask}
              onChange={(e) => setNewSubtask(e.target.value)}
              onPressEnter={addSubtask}
            />
            <Button type="default" onClick={addSubtask}>
              Add
            </Button>
          </Space.Compact>
        </div>
      </div>
    </Modal>
  );
};
